import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Alert } from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { FaqSidebar } from "components/shared/faq-sidebar";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { usePolicyDetail, usePolicyId } from "data/ss-data-hooks";
import { SelfServiceCenter } from "pages/documents/documents/self-service-center";
import { matchesCarrierGroupOrFlood, scrollToTop, trackEvent } from "utils";

import { AllPolicyDocs } from "./documents/all-policy-docs";
import { PaperlessDocumentsEnrollWidget } from "./documents/paperless-documents-enroll-widget";
import { PolicyDocs } from "./documents/policy-docs";

const HIDE_COVERAGE_CARRIERS = ["vave", "markel"];

export const Documents = () => {
  const { data } = usePolicyDetail();
  const isPolicyDataReady = !!data;

  const navigate = useNavigate();
  const ssPolicyId = usePolicyId();
  const { urlPolicyId } = useParams();

  const [searchParams] = useSearchParams();
  const documentsSigned = searchParams.get("documents_signed") === "true";
  const { carrierGroup, policyType } = data?.userPolicyProperties ?? {};
  const hideCoverage = matchesCarrierGroupOrFlood(
    carrierGroup,
    policyType,
    HIDE_COVERAGE_CARRIERS,
  );

  useEffect(() => {
    if (urlPolicyId !== ssPolicyId) {
      navigate(`/my/policies/${ssPolicyId}/documents`);
    }
  }, [urlPolicyId, ssPolicyId, navigate]);

  useEffect(() => {
    if (documentsSigned) {
      trackEvent("documents_selfServiceCenter_signSuccess");
    }
  }, [documentsSigned]);

  useEffect(() => {
    trackEvent("Documents");
    scrollToTop();
  }, []);

  if (!isPolicyDataReady) {
    return <BaseLayout loading />;
  }

  const Sidebar = () => (
    <Stack>
      <PaperlessDocumentsEnrollWidget />
      <InsuranceRepresentative />
      <FaqSidebar page="documents" />
    </Stack>
  );

  return (
    <BaseLayout
      tabs={[
        ...(hideCoverage
          ? []
          : [{ name: "Coverage", link: `/my/policies/${ssPolicyId}` }]),
        { name: "Documents", link: `/my/policies/${ssPolicyId}/documents` },
      ]}
      availableNotifications={["HomeServices", "CancellationPayment"]}
    >
      <MainLayout sidebar={<Sidebar />}>
        <Stack>
          {documentsSigned ? (
            <Alert
              title="Thank you!"
              description="We've successfully received your signed documents."
              appearance="success"
            />
          ) : null}
          <SelfServiceCenter />
          <PolicyDocs />
          <AllPolicyDocs />
        </Stack>
      </MainLayout>
    </BaseLayout>
  );
};
